/*
 *  Copyright (c) 2016 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */

/* eslint-env node */
'use strict';

var utils = require('../utils');

var logging = utils.log; // Expose public methods.

module.exports = function (window) {
  var browserDetails = utils.detectBrowser(window);
  var navigator = window && window.navigator;
  var MediaStreamTrack = window && window.MediaStreamTrack;

  var shimError_ = function shimError_(e) {
    return {
      name: {
        InternalError: 'NotReadableError',
        NotSupportedError: 'TypeError',
        PermissionDeniedError: 'NotAllowedError',
        SecurityError: 'NotAllowedError'
      }[e.name] || e.name,
      message: {
        'The operation is insecure.': 'The request is not allowed by the ' + 'user agent or the platform in the current context.'
      }[e.message] || e.message,
      constraint: e.constraint,
      toString: function toString() {
        return this.name + (this.message && ': ') + this.message;
      }
    };
  }; // getUserMedia constraints shim.


  var getUserMedia_ = function getUserMedia_(constraints, onSuccess, onError) {
    var constraintsToFF37_ = function constraintsToFF37_(c) {
      if (typeof c !== 'object' || c.require) {
        return c;
      }

      var require = [];
      Object.keys(c).forEach(function (key) {
        if (key === 'require' || key === 'advanced' || key === 'mediaSource') {
          return;
        }

        var r = c[key] = typeof c[key] === 'object' ? c[key] : {
          ideal: c[key]
        };

        if (r.min !== undefined || r.max !== undefined || r.exact !== undefined) {
          require.push(key);
        }

        if (r.exact !== undefined) {
          if (typeof r.exact === 'number') {
            r.min = r.max = r.exact;
          } else {
            c[key] = r.exact;
          }

          delete r.exact;
        }

        if (r.ideal !== undefined) {
          c.advanced = c.advanced || [];
          var oc = {};

          if (typeof r.ideal === 'number') {
            oc[key] = {
              min: r.ideal,
              max: r.ideal
            };
          } else {
            oc[key] = r.ideal;
          }

          c.advanced.push(oc);
          delete r.ideal;

          if (!Object.keys(r).length) {
            delete c[key];
          }
        }
      });

      if (require.length) {
        c.require = require;
      }

      return c;
    };

    constraints = JSON.parse(JSON.stringify(constraints));

    if (browserDetails.version < 38) {
      logging('spec: ' + JSON.stringify(constraints));

      if (constraints.audio) {
        constraints.audio = constraintsToFF37_(constraints.audio);
      }

      if (constraints.video) {
        constraints.video = constraintsToFF37_(constraints.video);
      }

      logging('ff37: ' + JSON.stringify(constraints));
    }

    return navigator.mozGetUserMedia(constraints, onSuccess, function (e) {
      onError(shimError_(e));
    });
  }; // Returns the result of getUserMedia as a Promise.


  var getUserMediaPromise_ = function getUserMediaPromise_(constraints) {
    return new Promise(function (resolve, reject) {
      getUserMedia_(constraints, resolve, reject);
    });
  }; // Shim for mediaDevices on older versions.


  if (!navigator.mediaDevices) {
    navigator.mediaDevices = {
      getUserMedia: getUserMediaPromise_,
      addEventListener: function addEventListener() {},
      removeEventListener: function removeEventListener() {}
    };
  }

  navigator.mediaDevices.enumerateDevices = navigator.mediaDevices.enumerateDevices || function () {
    return new Promise(function (resolve) {
      var infos = [{
        kind: 'audioinput',
        deviceId: 'default',
        label: '',
        groupId: ''
      }, {
        kind: 'videoinput',
        deviceId: 'default',
        label: '',
        groupId: ''
      }];
      resolve(infos);
    });
  };

  if (browserDetails.version < 41) {
    // Work around http://bugzil.la/1169665
    var orgEnumerateDevices = navigator.mediaDevices.enumerateDevices.bind(navigator.mediaDevices);

    navigator.mediaDevices.enumerateDevices = function () {
      return orgEnumerateDevices().then(undefined, function (e) {
        if (e.name === 'NotFoundError') {
          return [];
        }

        throw e;
      });
    };
  }

  if (browserDetails.version < 49) {
    var origGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

    navigator.mediaDevices.getUserMedia = function (c) {
      return origGetUserMedia(c).then(function (stream) {
        // Work around https://bugzil.la/802326
        if (c.audio && !stream.getAudioTracks().length || c.video && !stream.getVideoTracks().length) {
          stream.getTracks().forEach(function (track) {
            track.stop();
          });
          throw new DOMException('The object can not be found here.', 'NotFoundError');
        }

        return stream;
      }, function (e) {
        return Promise.reject(shimError_(e));
      });
    };
  }

  if (!(browserDetails.version > 55 && 'autoGainControl' in navigator.mediaDevices.getSupportedConstraints())) {
    var remap = function remap(obj, a, b) {
      if (a in obj && !(b in obj)) {
        obj[b] = obj[a];
        delete obj[a];
      }
    };

    var nativeGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

    navigator.mediaDevices.getUserMedia = function (c) {
      if (typeof c === 'object' && typeof c.audio === 'object') {
        c = JSON.parse(JSON.stringify(c));
        remap(c.audio, 'autoGainControl', 'mozAutoGainControl');
        remap(c.audio, 'noiseSuppression', 'mozNoiseSuppression');
      }

      return nativeGetUserMedia(c);
    };

    if (MediaStreamTrack && MediaStreamTrack.prototype.getSettings) {
      var nativeGetSettings = MediaStreamTrack.prototype.getSettings;

      MediaStreamTrack.prototype.getSettings = function () {
        var obj = nativeGetSettings.apply(this, arguments);
        remap(obj, 'mozAutoGainControl', 'autoGainControl');
        remap(obj, 'mozNoiseSuppression', 'noiseSuppression');
        return obj;
      };
    }

    if (MediaStreamTrack && MediaStreamTrack.prototype.applyConstraints) {
      var nativeApplyConstraints = MediaStreamTrack.prototype.applyConstraints;

      MediaStreamTrack.prototype.applyConstraints = function (c) {
        if (this.kind === 'audio' && typeof c === 'object') {
          c = JSON.parse(JSON.stringify(c));
          remap(c, 'autoGainControl', 'mozAutoGainControl');
          remap(c, 'noiseSuppression', 'mozNoiseSuppression');
        }

        return nativeApplyConstraints.apply(this, [c]);
      };
    }
  }

  navigator.getUserMedia = function (constraints, onSuccess, onError) {
    if (browserDetails.version < 44) {
      return getUserMedia_(constraints, onSuccess, onError);
    } // Replace Firefox 44+'s deprecation warning with unprefixed version.


    utils.deprecated('navigator.getUserMedia', 'navigator.mediaDevices.getUserMedia');
    navigator.mediaDevices.getUserMedia(constraints).then(onSuccess, onError);
  };
};